import React, { useState, useEffect } from 'react';
import API from '../services/api';

function AdminGenerateIntakeLinkPage() {
  // For the existing “search + link generation”
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // For creating a new client
  const [allTherapists, setAllTherapists] = useState([]);
  const [newClientName, setNewClientName] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [assignedTherapistId, setAssignedTherapistId] = useState('');

  // 1) Load all clients + therapists on mount
  useEffect(() => {
    fetchAllClients();
    fetchAllTherapists();
  }, []);

  const fetchAllClients = async () => {
    try {
      const res = await API.get('/admin/all-clients');
      setAllClients(res.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      alert('Could not fetch clients');
    }
  };

  const fetchAllTherapists = async () => {
    try {
      const res = await API.get('/admin/all-therapists');
      setAllTherapists(res.data);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      alert('Could not fetch therapists');
    }
  };

  // 2) Filter displayed clients
  const displayedClients = searchTerm.trim()
    ? allClients.filter((c) =>
        c.client_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allClients;

  // 3) Handle selecting a client
  const handleSelectClient = (client) => {
    setSelectedClientId(client.id);
    setSelectedClientName(client.client_name);
    setSearchTerm(client.client_name);
    setGeneratedLink('');
    setErrorMsg('');
  };

  // 4) When user types in the search box, clear previous selection
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setSelectedClientId(null);
    setSelectedClientName('');
    setGeneratedLink('');
    setErrorMsg('');
  };

  // 5) Actually generate the intake link
  const handleGenerateLink = async () => {
    if (!selectedClientId) {
      setErrorMsg('Please select a client first.');
      return;
    }
    try {
      const body = {
        clientId: selectedClientId,
        expiresAt: expiresAt || null
      };

      // Updated to remove the hard-coded localhost
      const res = await API.post('/forms/intake/generate', body);

      if (res.data.success) {
        setGeneratedLink(res.data.link);
        setErrorMsg('');
      } else {
        setErrorMsg(res.data.error || 'Error generating link.');
      }
    } catch (err) {
      console.error('Error generating link:', err);
      setErrorMsg('Server error generating link.');
    }
  };

  // =========== NEW: Create New Client ===========
  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      alert('Please enter a client name');
      return;
    }
    if (!assignedTherapistId) {
      alert('Please select a therapist to assign');
      return;
    }

    try {
      // We'll assume your "POST /admin/clients" accepts
      // { clientName, phone, email, therapistOwnerId }
      // or something similar. Let's add phone+email as well.

      const body = {
        clientName: newClientName.trim(),
        phone: newClientPhone || null,
        email: newClientEmail || null,
        therapistOwnerId: assignedTherapistId
      };

      const res = await API.post('/admin/clients', body);
      if (res.data.message === 'Client created successfully') {
        // 1) Refresh client list
        fetchAllClients();

        // 2) Optionally auto-select this new client for immediate intake link
        alert('Client created! They are now in the list below.');

        // Clear out the form
        setNewClientName('');
        setNewClientPhone('');
        setNewClientEmail('');
        setAssignedTherapistId('');
      } else {
        alert('Error creating client: ' + (res.data.message || 'Unknown'));
      }
    } catch (err) {
      console.error('Error creating new client:', err);
      alert('Server error creating client');
    }
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>New Client Intake</h2>

      {/* =========== CREATE NEW CLIENT SECTION =========== */}
      <div
        style={{
          marginBottom: '2rem',
          padding: '1rem',
          border: '1px solid var(--border-color)'
        }}
      >
        <h3>Create New Client</h3>
        <div style={{ marginBottom: '0.5rem' }}>
          <label>Client Name:</label>
          <br />
          <input
            type="text"
            value={newClientName}
            onChange={(e) => setNewClientName(e.target.value)}
            placeholder="e.g. John Doe"
            style={{ width: '200px' }}
          />
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <label>Phone:</label>
          <br />
          <input
            type="text"
            value={newClientPhone}
            onChange={(e) => setNewClientPhone(e.target.value)}
            placeholder="Optional phone number"
            style={{ width: '200px' }}
          />
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <label>Email:</label>
          <br />
          <input
            type="email"
            value={newClientEmail}
            onChange={(e) => setNewClientEmail(e.target.value)}
            placeholder="Optional email"
            style={{ width: '200px' }}
          />
        </div>
        <div style={{ marginBottom: '0.5rem' }}>
          <label>Assign Therapist:</label>
          <br />
          <select
            value={assignedTherapistId}
            onChange={(e) => setAssignedTherapistId(e.target.value)}
          >
            <option value="">-- Choose Therapist --</option>
            {allTherapists.map((t) => (
              <option key={t.id} value={t.id}>
                {t.last_name}, {t.first_name}
              </option>
            ))}
          </select>
        </div>
        <button onClick={handleCreateClient}>Create Client</button>
      </div>

      {/* =========== SEARCH + LIST FOR GENERATING LINK =========== */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        {/* LEFT: search input */}
        <div>
          <label>Search Client:</label>
          <br />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Type to search by client name..."
            style={{ width: '200px' }}
          />
          {selectedClientName && (
            <div style={{ marginTop: '0.5rem' }}>
              <strong>Selected Client:</strong> {selectedClientName}
            </div>
          )}
        </div>

        {/* RIGHT: list of matching clients */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            width: '220px',
            height: '250px',
            overflowY: 'auto',
            padding: '0.5rem',
          }}
        >
          {displayedClients.length === 0 ? (
            <div style={{ color: '#999' }}>No clients found</div>
          ) : (
            displayedClients.map((client) => (
              <div
                key={client.id}
                style={{
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--border-color)',
                  padding: '0.25rem 0',
                }}
                onClick={() => handleSelectClient(client)}
              >
                {client.client_name}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ExpiresAt + Generate Link */}
      <div style={{ marginTop: '1rem' }}>
        <label>Expires At (optional):</label>
        <br />
        <input
          type="datetime-local"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
        />
      </div>

      {errorMsg && (
        <p style={{ color: 'red', marginTop: '0.5rem' }}>{errorMsg}</p>
      )}

      <button onClick={handleGenerateLink} style={{ marginTop: '1rem' }}>
        Generate Link
      </button>

      {/* Show generated link if any */}
      {generatedLink && (
        <div style={{ marginTop: '1rem' }}>
          <p><strong>Intake Link:</strong></p>
          <a href={generatedLink} target="_blank" rel="noreferrer">
            {generatedLink}
          </a>
        </div>
      )}
    </div>
  );
}

export default AdminGenerateIntakeLinkPage;
